import { ArrowLeftIcon, LogOutIcon, type LucideIcon } from "lucide-react";
import { Link, useLocation } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { CHAT_PATH } from "@/constants";
import { homePath } from "@/pages/SEmbed/fullAppPaths";
import { useT, type MessageKey } from "@/i18n";

export type SettingsSidebarItem = { label: MessageKey; icon: LucideIcon; path: string };

export type SettingsSidebarGroup = { label: MessageKey; items: SettingsSidebarItem[] };

/** The Settings sidebar, laid out like S's own Settings: Back to Home, then the pages. */
export function SettingsSidebar({ groups }: { groups: SettingsSidebarGroup[] }) {
  const location = useLocation();
  const { setOpenMobile } = useSidebar();
  const { t, dir } = useT();

  return (
    // Arabic opens the sidebar from the right, as S does.
    <Sidebar
      variant="floating"
      collapsible="offcanvas"
      side={dir === "rtl" ? "right" : "left"}
      dir={dir}
    >
      <SidebarHeader className="gap-3 p-1.5 pt-2">
        <Button asChild size="sm" variant="ghost" className="self-start">
          {/* Returns to where the user was in S, not to S's start page. */}
          <Link to={homePath(CHAT_PATH)} onClick={() => setOpenMobile(false)}>
            <ArrowLeftIcon data-icon="inline-start" className="rtl:rotate-180" />
            {t("settings.backToHome")}
          </Link>
        </Button>
        <h2 className="px-2 text-title font-title">{t("settings.title")}</h2>
      </SidebarHeader>
      <SidebarContent className="px-2">
        <nav aria-label={t("settings.pages")}>
          {groups.map((group) => (
            <SidebarGroup key={group.label}>
              <SidebarGroupLabel>{t(group.label)}</SidebarGroupLabel>
              <SidebarMenu className="gap-1">
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton
                      asChild
                      isActive={location.pathname.endsWith(`/${item.path}`)}
                    >
                      <Link
                        to={item.path}
                        onClick={() => setOpenMobile(false)}
                        aria-current={
                          location.pathname.endsWith(`/${item.path}`) ? "page" : undefined
                        }
                      >
                        <item.icon />
                        <span>{t(item.label)}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroup>
          ))}
        </nav>
      </SidebarContent>
      <SidebarFooter className="p-2">
        <Button asChild variant="ghost" className="justify-start text-destructive">
          <Link to={`${location.pathname}?logout`} onClick={() => setOpenMobile(false)}>
            <LogOutIcon data-icon="inline-start" className="rtl:rotate-180" />
            {t("signOut.title")}
          </Link>
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}
